import React, { useState, useMemo, useCallback } from 'react'

import {
  Button,
  Tooltip,
  Icon,
  InfoOutlined,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { func } from 'prop-types'

import CustomSelect from 'Pods/Shared/CustomSelect/CustomSelect'
import LegacyDataTable from 'Pods/Shared/DataTable/LegacyDataTable'

import { NOOP } from 'Utils/stableDefaults'

import { renderMoneyValue, renderPercentage } from '../../../formatter'
import { useMonthlySummary } from '../../../Hooks/useMonthlySummary'
import { PERIOD_OPTIONS } from '../../../Static/constants'
import EmptyMessage from '../../EmptyMessage'

const SUBSCRIPTION_EARNINGS_HEADER_TOOLTIP =
  'Subscription earnings are calculated after each month closes. ' +
  'Eligible monthly earnings will appear at the start of the following month.'

const SUBSCRIPTION_EARNINGS_PENDING_TOOLTIP =
  'Subscription earnings are calculated in the beginning of the following month. ' +
  'Any earnings from this month will appear here once the month closes.'

const renderSubscriptionEarningsCell = (value, row) => {
  if (row.isCurrentMonth) {
    return (
      <Tooltip content={SUBSCRIPTION_EARNINGS_PENDING_TOOLTIP} place="top">
        <span className="overview-monthly-summary__pending">
          Available after month ends
        </span>
      </Tooltip>
    )
  }
  return renderMoneyValue(value)
}

const subscriptionEarningsTitle = (
  <span className="overview-monthly-summary__header-label">
    Subscription earnings
    <Icon
      svgComponent={InfoOutlined}
      color="dark"
      size="small"
    />
  </span>
)

const columnsConfig = {
  id: { visible: false },
  sales: { visible: false },
  downloads: { visible: false },
  finalEarnings: { visible: false },
  yearMonth: { visible: false },
  isCurrentMonth: { visible: false },
  date: { title: 'Date', position: 1 },
  soldFor: { title: 'Sales ($)', position: 2 },
  earningRate: { title: 'Avg. Earning Rate', position: 3 },
  revenue: { title: 'Earnings', position: 4 },
  subscriptionEarnings: {
    title: subscriptionEarningsTitle,
    tooltip: SUBSCRIPTION_EARNINGS_HEADER_TOOLTIP,
    position: 5,
  },
  donations: { title: 'Donations', position: 6 },
  totalFinalEarnings: { title: 'Final Earnings', position: 7 },
  actions: { title: 'Actions', position: 8 },
}

function enrichRowsWithSubscriptionEarnings(data) {
  return data
    .filter((row) => row.date !== 'Last 12 months')
    .map((row) => {
      const subscriptionEarnings = Number(row.subscriptionEarnings) || 0
      const legacyFinalEarnings = Number(row.finalEarnings) || 0

      return {
        ...row,
        subscriptionEarnings,
        totalFinalEarnings: legacyFinalEarnings + subscriptionEarnings,
        actions: '',
      }
    })
}

function renderContent({ isLoading, error, tableData, customCellRenderers }) {
  if (isLoading) {
    return <div className="overview-monthly-summary__placeholder" />
  }

  if (error) {
    return (
      <EmptyMessage
        title="Unable to load monthly summary"
        description="Please try again later."
      />
    )
  }

  return (
    <LegacyDataTable
      data={tableData}
      columnsConfig={columnsConfig}
      EmptyMessage={<EmptyMessage title="No monthly summary available!" />}
      renderCustomCell={customCellRenderers}
    />
  )
}

function MonthlySummarySection({ onViewDetails = NOOP }) {
  const [period, setPeriod] = useState(PERIOD_OPTIONS[2])
  const { monthlySummary, isLoading, error } = useMonthlySummary(period.value)

  const tableData = useMemo(
    () => enrichRowsWithSubscriptionEarnings(monthlySummary),
    [monthlySummary]
  )

  const renderActions = useCallback((_value, row) => (
    <Button
      type="ghost-primary"
      size="small"
      customClass="overview-monthly-summary__action"
      onClick={() => onViewDetails(row.yearMonth)}
      disabled={!row.yearMonth}
    >
      View details
    </Button>
  ), [onViewDetails])

  const customCellRenderers = {
    revenue: renderMoneyValue,
    subscriptionEarnings: renderSubscriptionEarningsCell,
    donations: renderMoneyValue,
    earningRate: renderPercentage,
    soldFor: renderMoneyValue,
    totalFinalEarnings: renderMoneyValue,
    actions: renderActions,
  }

  return (
    <div className="overview-monthly-summary">
      <div className="overview-monthly-summary__header">
        <h2 className="cgt-heading-h4">Monthly Summary</h2>
        <CustomSelect
          className="overview-select"
          valueClassName="cgt-body-base-semibold"
          options={PERIOD_OPTIONS}
          onChange={setPeriod}
          value={period}
          ariaLabel="Monthly summary time range"
        />
      </div>

      {renderContent({ isLoading, error, tableData, customCellRenderers })}
    </div>
  )
}

MonthlySummarySection.propTypes = {
  onViewDetails: func,
}

export default MonthlySummarySection
